import { dayMonth, parseDay, todayStr, weekDays } from "./date";
import { moveById, position } from "./moves";
import { typeName } from "./plan";
import { latestTests } from "./fitnessTests";
import { mealWalkWords, steadyWeekFor, walkDayName } from "./programme";
import { walkSecs } from "./walk";
import { weekRows, type WeekRow } from "./week";
import type { Block, KayaState, MoveTag, SessionKey } from "./types";

/*
 * The Steady screens' words and figures (boards TrainSteady, TrainSessionSteady, TrainWeek;
 * ADR-052): pure, so the pages only lay them out. No streaks, no "missed", no kcal — a parent
 * sees sessions done, minutes and the chair-stand count (plan §1.15, §3.4).
 */

/** Minutes a walk after a meal is planned for (plan §3.3). */
export const MEAL_WALK_MIN = 10;

const WHERE: Partial<Record<MoveTag, string>> = {
  chair: "the chair",
  support: "the counter",
  wall: "the wall",
  standing: "standing",
  bed: "the bed",
};

/** Where a block happens, in the order its moves go: "seated", "at the counter", "the chair,
 *  then the counter"; a block that ends on the chair after standing "ends sitting". */
export function blockWhere(b: Block, last = false): string {
  const pos: MoveTag[] = [];
  for (const it of b.items) {
    const ex = moveById(it.id);
    const p = ex ? position(ex) : null;
    if (p && pos[pos.length - 1] !== p) pos.push(p);
  }
  const uniq = Array.from(new Set(pos));
  // the counter and the wall are both "something to hold": one place in words
  if (uniq.length > 1 && uniq.every((p) => p === "support" || p === "wall")) return "at the counter";
  if (last && pos.length > 1 && (pos[pos.length - 1] === "chair" || pos[pos.length - 1] === "bed")) return "ends sitting";
  if (uniq.length === 1) {
    const p = uniq[0];
    return p === "chair" ? "seated" : p === "bed" ? "in bed" : p === "support" ? "at the counter" : p === "wall" ? "at the wall" : "standing";
  }
  return uniq
    .slice(0, 2)
    .map((p) => WHERE[p] ?? p)
    .join(", then ");
}

/** "chair and counter" for the hero: the places a session uses, in words. */
export function sessionWhere(blocks: Block[]): string {
  const places = new Set<string>();
  for (const b of blocks) {
    for (const it of b.items) {
      const ex = moveById(it.id);
      // the breath is taken wherever you are sitting: it names no place
      const p = ex && !ex.tags?.includes("breath") ? position(ex) : null;
      if (p === "chair") places.add("chair");
      else if (p === "support" || p === "wall" || p === "standing") places.add("counter");
      else if (p === "bed") places.add("bed");
    }
  }
  const order = ["bed", "chair", "counter"].filter((p) => places.has(p));
  return order.length ? order.join(" and ") : "";
}

export interface SteadyDayRow {
  ds: string;
  key: SessionKey;
  /** "Strength & balance", "Joints + 2 walks", "Rest" */
  name: string;
  /** "after lunch and dinner" on a walk day; "Today" first when it is today */
  sub: string | null;
  /** the session's real length plus the planned walks; null on a rest day */
  minutes: number | null;
  done: boolean;
  started: boolean;
  isToday: boolean;
  rest: boolean;
  pct: number;
}

function walksPlanned(S: KayaState, key: SessionKey): number {
  return key === "MJ" ? (S.profile.training?.mealWalks?.length ?? 0) : 0;
}

/** The week, Monday first, as the Steady screens list it. */
export function steadyWeekRows(S: KayaState, today = todayStr(), simple?: boolean): SteadyDayRow[] {
  return weekRows(S, today, simple).map((r: WeekRow) => {
    const rest = r.key === "R";
    const walkName = walkDayName(S, r.key);
    const walks = mealWalkWords(S);
    const subs: string[] = [];
    if (r.isToday) subs.push("Today");
    if (walkName && walks) subs.push(walks);
    const n = walksPlanned(S, r.key);
    return {
      ds: r.ds,
      key: r.key,
      name: rest ? "Rest" : walkName ?? typeName(r.key),
      sub: subs.length ? subs.join(" · ") : null,
      minutes: rest ? null : r.minutes + n * MEAL_WALK_MIN,
      done: r.state === "done" || r.state === "rescued",
      started: r.state === "started",
      isToday: r.isToday,
      rest,
      pct: r.pct,
    };
  });
}

export interface SteadyFigures {
  strengthDone: number;
  strengthOf: number;
  /** the week's walks so far, minutes */
  walkMin: number;
  /** the latest chair-stand count and its day, or null */
  chairStand: { n: number; ds: string } | null;
  /** the week's planned minutes */
  plannedMin: number;
}

const STRENGTH_KEYS: readonly SessionKey[] = ["SB", "CH"];

export function steadyFigures(S: KayaState, today = todayStr(), simple?: boolean): SteadyFigures {
  const rows = steadyWeekRows(S, today, simple);
  const strength = rows.filter((r) => STRENGTH_KEYS.includes(r.key));
  const days = weekDays(parseDay(today)).filter((ds) => ds <= today);
  const walkMin = Math.round(days.reduce((a, ds) => a + walkSecs(S.logs[ds]?.walks), 0) / 60);
  const lt = latestTests(S, today);
  const cs = lt && typeof lt.tests.chairStand === "number" ? { n: lt.tests.chairStand, ds: lt.ds } : null;
  return {
    strengthDone: strength.filter((r) => r.done).length,
    strengthOf: strength.length,
    walkMin,
    chairStand: cs,
    plannedMin: rows.reduce((a, r) => a + (r.minutes ?? 0), 0),
  };
}

/** "3 strength days, walks after meals" — the week's title. */
export function steadyWeekTitle(S: KayaState, today = todayStr()): string {
  const w = steadyWeekFor(S, today) ?? [];
  const n = w.filter((k) => STRENGTH_KEYS.includes(k)).length;
  const meals = (S.profile.training?.mealWalks?.length ?? 0) > 0;
  const walkDays = w.filter((k) => k === "WK").length;
  const tail = meals ? ", walks after meals" : walkDays ? `, walks on ${walkDays} days` : "";
  return `${n} strength day${n === 1 ? "" : "s"}${tail}`;
}

/** "Mon 5 – Sun 11 Oct" */
export function weekSpan(today = todayStr()): string {
  const days = weekDays(parseDay(today));
  const a = parseDay(days[0]);
  const b = parseDay(days[6]);
  const first = a.getMonth() === b.getMonth() ? `Mon ${a.getDate()}` : `Mon ${dayMonth(days[0])}`;
  return `${first} – Sun ${dayMonth(days[6])}`;
}
